import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { crlApi } from '../../services/api';

function EmailAlerts() {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadSubscribers();
  }, []);

  const loadSubscribers = async () => {
    try {
      setLoading(true);
      const response = await crlApi.getSubscribers();
      setSubscribers(response.data.subscribers || []);
    } catch (err) {
      setError('Failed to load subscribers.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;

    try {
      setSubmitting(true);
      await crlApi.subscribe(newEmail.trim());
      setNewEmail('');
      setSnackbar({ open: true, message: 'Subscriber added successfully!', severity: 'success' });
      loadSubscribers();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to add subscriber.', severity: 'error' });
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnsubscribe = async (email) => {
    if (!window.confirm(`Remove ${email} from subscribers?`)) return;

    try {
      await crlApi.unsubscribe(email);
      setSnackbar({ open: true, message: 'Subscriber removed successfully!', severity: 'success' });
      loadSubscribers();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to remove subscriber.', severity: 'error' });
      console.error(err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        CRL Email Alerts
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Manage email subscribers who receive notifications when new CRL letters are found.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Add Subscriber Form */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Add Subscriber
        </Typography>
        <Box component="form" onSubmit={handleSubscribe} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <TextField
            type="email"
            label="Email Address"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            size="small"
            required
            sx={{ minWidth: 300 }}
          />
          <Button
            type="submit"
            variant="contained"
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
            disabled={submitting || !newEmail.trim()}
          >
            Add
          </Button>
        </Box>
      </Paper>

      {/* Subscribers List */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Subscribed Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subscribers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography color="text.secondary">No subscribers yet.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                subscribers.map((sub) => (
                  <TableRow key={sub.email}>
                    <TableCell>{sub.email}</TableCell>
                    <TableCell>{formatDate(sub.subscribed_at)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="error"
                        onClick={() => handleUnsubscribe(sub.email)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default EmailAlerts;
